// Generated from Swagger on 2025-08-27T14:46:24.226Z
import {
  useSuspenseQuery,
  useQuery,
  useMutation,
  useQueryClient,
  UseSuspenseQueryOptions,
  UseQueryOptions,
  UseMutationOptions,
} from "@tanstack/react-query";
import { useContext } from "react";
import { useApiClient } from "../client";
import { creationEntitiesKeys } from "../creation-entities-keys";
import { formatSearchQuery } from "../../utils/format-search-query";
import { Context } from "@/util-components/root/context";
import {
  MatchListPaginatedResponseDtoWithListEntities,
  CreateMatchRequestDto,
  MatchResponseDto,
  MatchCreationEntitiesResponseDto,
  MatchDetailsResponseDto,
  UpdateMatchRequestDto,
} from "../interfaces/matches.interface";

export interface UseMatchesFindParams {
  /**
   * The text to search for
   * @constraints minLength: 4, example: Jobdescription
   */
  textSearch?: string;
  /**
   * Team ID
   */
  teamId?: string;
  /**
   * Filter type
   * @constraints default: all, example: past
   */
  filterType?: "past" | "upcoming" | "all";
  /**
   * Filter by Home or Away
   */
  homeOrAway?: "HOME" | "AWAY";
  /**
   * Filter by League Name
   */
  leagueName?: string;
  /**
   * The maximum amount of retrieved items per page
   * @constraints minimum: 1, maximum: 20, default: 20, example: 20
   */
  limit?: number;
  /**
   * The page number to retrieve items from.
   * @constraints minimum: 1, default: 1, example: 1
   */
  page?: number;
  /**
   * Sort field
   */
  sort?: string;
  /**
   * Sort order
   */
  order?: "asc" | "desc";
}

// Query keys for matches-related queries
export const matchesKeys = {
  all: ["matches"] as const,
  profile: () => [...matchesKeys.all, "profile"] as const,
  mutation: () => [...matchesKeys.all, "mutation"] as const,
  creation_entities: () => [...matchesKeys.all, "creation_entities"] as const,
  details: () => [...matchesKeys.all, "detail"] as const,
  detail: (id: string) => [...matchesKeys.details(), id] as const,
  detail_mutation: (params?: any) =>
    [...matchesKeys.all, "detail_mutation", params] as const,
};

/**
 * Find all matches with filter
 */
export function useMatchesFind(
  params?: UseMatchesFindParams,
  options?: Omit<
    UseSuspenseQueryOptions<MatchListPaginatedResponseDtoWithListEntities>,
    "queryKey" | "queryFn"
  > & { skipGlobalErrorHandler?: boolean },
) {
  const { apiRequest } = useApiClient();
  const { skipGlobalErrorHandler, ...queryOptions } = options || {};

  return useSuspenseQuery({
    queryKey: [
      ...matchesKeys.profile(),
      ...creationEntitiesKeys.list(),
      params,
    ],
    queryFn: () => {
      const queryString = formatSearchQuery(params);
      return apiRequest<MatchListPaginatedResponseDtoWithListEntities>(
        `/matches${queryString}`,
        "GET",
        undefined,
        undefined,
        { skipGlobalErrorHandler }
      );
    },
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0, // replaces cacheTime in v5
    refetchOnMount: "always",
    ...queryOptions,
  });
}

/**
 * Non-suspense version of useMatchesFind
 */
export function useMatchesFindQuery(
  params?: UseMatchesFindParams,
  options?: Omit<
    UseQueryOptions<MatchListPaginatedResponseDtoWithListEntities>,
    "queryKey" | "queryFn"
  > & { skipGlobalErrorHandler?: boolean },
) {
  const { apiRequest } = useApiClient();
  const { skipGlobalErrorHandler, ...queryOptions } = options || {};

  return useQuery({
    queryKey: [
      ...matchesKeys.profile(),
      ...creationEntitiesKeys.list(),
      params,
    ],
    queryFn: () => {
      const queryString = formatSearchQuery(params);
      return apiRequest<MatchListPaginatedResponseDtoWithListEntities>(
        `/matches${queryString}`,
        "GET",
        undefined,
        undefined,
        { skipGlobalErrorHandler }
      );
    },
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0, // replaces cacheTime in v5
    refetchOnMount: "always",
    refetchOnReconnect: true,
    refetchOnWindowFocus: false,
    ...queryOptions,
  });
}

/**
 * Create a new match
 */
export function useMatchesCreate(
  options?: Omit<
    UseMutationOptions<MatchResponseDto, Error, CreateMatchRequestDto>,
    "mutationFn"
  >,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();
  const { refetchUser } = useContext(Context);

  return useMutation({
    ...options,
    mutationFn: (data: CreateMatchRequestDto) =>
      apiRequest<MatchResponseDto>(`/matches`, "POST", data),
    onSuccess: (data, variables, context) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: matchesKeys.all });
      // Invalidate all queries that contain creation entities keys
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey as string[];
          return queryKey.includes("creation-entities");
        },
      });
      void refetchUser?.();
      // Call user-provided onSuccess if it exists
      options?.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Get creation entities
 */
export function useMatchesCreationEntities(
  options?: Omit<
    UseSuspenseQueryOptions<MatchCreationEntitiesResponseDto>,
    "queryKey" | "queryFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useSuspenseQuery({
    queryKey: creationEntitiesKeys.list(),
    queryFn: () =>
      apiRequest<MatchCreationEntitiesResponseDto>(
        `/matches/creation-entities`,
      ),
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0, // replaces cacheTime in v5
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Non-suspense version of useMatchesCreationEntities
 */
export function useMatchesCreationEntitiesQuery(
  options?: Omit<
    UseQueryOptions<MatchCreationEntitiesResponseDto>,
    "queryKey" | "queryFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useQuery({
    queryKey: creationEntitiesKeys.list(),
    queryFn: () =>
      apiRequest<MatchCreationEntitiesResponseDto>(
        `/matches/creation-entities`,
      ),
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0, // replaces cacheTime in v5
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Find one match
 */
export function useMatchesFindOne(
  id: string,
  options?: Omit<
    UseSuspenseQueryOptions<MatchDetailsResponseDto>,
    "queryKey" | "queryFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useSuspenseQuery({
    queryKey: [...matchesKeys.detail(id), ...creationEntitiesKeys.list()],
    queryFn: () => apiRequest<MatchDetailsResponseDto>(`/matches/${id}`),
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0, // replaces cacheTime in v5
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Non-suspense version of useMatchesFindOne
 */
export function useMatchesFindOneQuery(
  id: string,
  options?: Omit<
    UseQueryOptions<MatchDetailsResponseDto>,
    "queryKey" | "queryFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useQuery({
    queryKey: [...matchesKeys.detail(id), ...creationEntitiesKeys.list()],
    queryFn: () => apiRequest<MatchDetailsResponseDto>(`/matches/${id}`),
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0, // replaces cacheTime in v5
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Update one match
 */
export function useMatchesUpdate(
  options?: Omit<
    UseMutationOptions<any, Error, { id: string; data: UpdateMatchRequestDto }>,
    "mutationFn"
  >,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ id, data }: { id: string; data: UpdateMatchRequestDto }) =>
      apiRequest<any>(`/matches/${id}`, "PATCH", data),
    onSuccess: (data, variables, context) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: matchesKeys.all });
      // Invalidate all queries that contain creation entities keys
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey as string[];
          return queryKey.includes("creation-entities");
        },
      });
      // Call user-provided onSuccess if it exists
      options?.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Delete one match
 */
export function useMatchesDelete(
  options?: Omit<UseMutationOptions<any, Error, { id: string }>, "mutationFn">,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ id }: { id: string }) =>
      apiRequest<any>(`/matches/${id}`, "DELETE"),
    onSuccess: (data, variables, context) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: matchesKeys.all });
      // Invalidate all queries that contain creation entities keys
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey as string[];
          return queryKey.includes("creation-entities");
        },
      });
      // Call user-provided onSuccess if it exists
      options?.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Delete multiple matches
 */
export function useMatchesBulkDelete(
  options?: Omit<UseMutationOptions<void, Error, { ids: string[] }>, "mutationFn">,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ ids }: { ids: string[] }) =>
      apiRequest<void>(`/matches/bulk-delete`, "POST", { ids }),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: matchesKeys.all });
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey as string[];
          return queryKey.includes("creation-entities");
        },
      });
      options?.onSuccess?.(data, variables, context);
    },
  });
}
